// src/lib/analytics.ts - PostHog analytics
import posthog from "posthog-js";

const POSTHOG_KEY = "phc_35tOhGtgd3XFRi9fkWBNG2ZSPnz8UxkbsiwPpbBFsoy";
const isLocal =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1");

type ConsentDetail = { granted: boolean; regulated: boolean };

// Consent state comes from public/consent.js (window.__a2aiConsent + the
// "a2ai-consent" event). Consented visitors get the normal cookie-based setup;
// EU/UK decliners get cookieless mode (memory persistence, no identify) so
// aggregate counts survive without anything stored on their device.
let cookielessMode = false;

function startPostHog(detail: ConsentDetail) {
  cookielessMode = !detail.granted;
  posthog.init(POSTHOG_KEY, {
    api_host: "https://us.i.posthog.com",
    autocapture: true,
    capture_pageview: true,
    capture_pageleave: true,
    ...(cookielessMode ? { persistence: "memory" as const } : {}),
  });
}

// Initialize PostHog (call once at app startup)
export function initAnalytics() {
  if (isLocal) return; // Don't track localhost
  const existing = (window as unknown as { __a2aiConsent?: ConsentDetail }).__a2aiConsent;
  if (existing) {
    startPostHog(existing);
    return;
  }
  document.addEventListener(
    "a2ai-consent",
    (e) => startPostHog((e as CustomEvent<ConsentDetail>).detail),
    { once: true },
  );
}

// Identify a logged-in user (call after Clerk auth)
export function identifyUser(userId: string, traits?: Record<string, string>) {
  if (isLocal || cookielessMode) return;
  posthog.identify(userId, traits);
}

// Reset on logout
export function resetUser() {
  if (isLocal) return;
  posthog.reset();
}

// Track a custom event
export const trackEvent = (event: string, properties?: Record<string, unknown>) => {
  if (isLocal) return;
  posthog.capture(event, properties);
};

// Predefined event names for consistency
export const ANALYTICS_EVENTS = {
  // Trends page events
  TRENDS: {
    TIME_RANGE: (days: string) => `trends_time_range_${days}`,
    CHART_TYPE: (type: string) => `trends_chart_type_${type}`,
    METRIC_TOGGLE: (metric: string) => `trends_metric_toggle_${metric}`,
    VIEW_MODE: (mode: string) => `trends_view_mode_${mode}`,
  },
  // Hourly page events
  HOURLY: {
    TIME_RANGE: (range: string) => `hourly_timerange_${range}`,
    VIEW_TYPE: (type: string) => `hourly_view_${type}`,
    CHART_TYPE: (type: string) => `hourly_chart_${type}`,
    METRIC_TOGGLE: (metric: string) => `hourly_metric_toggle_${metric}`,
    ANALYSIS_VIEW: (view: string) => `hourly_analysis_${view}`,
    DOW_TIMERANGE: (range: string) => `hourly_dow_timerange_${range}`,
    HEATMAP_THEME: (theme: string) => `hourly_heatmap_theme_${theme}`,
    ROWS_PER_PAGE: (rows: number) => `hourly_rows_per_page_${rows}`,
  },
  // Analysis page events
  ANALYSIS: {
    TIME_RANGE: (range: string) => `analysis_timerange_${range}`,
    OPTIMIZATION_MODE: (mode: string) => `analysis_optimization_${mode}`,
    TAB_CHANGE: (tab: string) => `analysis_tab_${tab}`,
    ROWS_PER_PAGE: (rows: number) => `analysis_rows_per_page_${rows}`,
    CAMPAIGN_FILTER: (type: "include" | "exclude") =>
      `analysis_filter_${type}`,
  },
  // Settings page events
  SETTINGS: {
    ACCOUNT_EDIT: (action: string) => `settings_account_${action}`,
    BUSINESS_MODE: (mode: string) => `settings_business_mode_${mode}`,
    CURRENCY_CHANGE: (currency: string) => `settings_currency_${currency}`,
    REFRESH_DATA: () => "settings_refresh_data",
    DELETE_ACCOUNT: () => "settings_delete_account",
  },
  // Curve page events
  CURVE: {
    CHART_TYPE: (type: string) => `curve_chart_type_${type}`,
    RESPONSE_MODEL: (model: string) => `curve_response_model_${model}`,
    CAMPAIGN_SELECT: () => `curve_campaign_select`,
    COST_CHANGE: () => `curve_cost_change`,
    VALUE_CHANGE: () => `curve_value_change`,
    CONVERSIONS_CHANGE: () => `curve_conversions_change`,
  },
  // Placements page events
  PLACEMENTS: {
    CAMPAIGN_SELECT: () => "placements_campaign_select",
    TYPE_FILTER: (type: string) => `placements_type_filter_${type}`,
    PLACEMENT_FILTER: () => "placements_placement_filter",
    ROWS_PER_PAGE: (rows: number) => `placements_rows_per_page_${rows}`,
    EXCLUDE_TOGGLE: () => "placements_exclude_toggle",
    COPY_EXCLUSIONS: () => "placements_copy_exclusions",
    BULK_EXCLUDE: (type: "all" | "visible") =>
      `placements_bulk_exclude_${type}`,
    COPY_APP_CATEGORIES: () => "placements_copy_app_categories",
  },
  // Help page events
  HELP: {
    STORY_VIEW: () => "help_story_view",
    TUTORIAL_VIEW: () => "help_tutorial_view",
    FEEDBACK_CLICK: () => "help_feedback_click",
    CHALLENGE_START: () => "help_challenge_start",
    CURVE_STORY: () => "help_curve_story",
  },
} as const;
